/**
 * Media store — отдельный persistent state для /process/media (ТЗ 2026-09-22).
 *
 * Режим 1 «Поиск фото» + режим 2 «Генерация картинок»: датасет/тариф/тест-прогон,
 * пользовательский текст и выбранные колонки, текущий прогон.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type MediaTierKey = 'economy' | 'optimal' | 'max';
export type MediaMode = 'image_search' | 'image_generation';

interface MediaState {
  mode: MediaMode;
  datasetId: string | null;
  tierKey: MediaTierKey;
  isTestRun: boolean;
  testRunLimit: number;
  currentRunId: string | null;
  userNote: string;
  userText: string;
  style: string;
  selectedColumns: string[];
  imageCount: number;

  setMode: (m: MediaMode) => void;
  setDatasetId: (id: string | null) => void;
  setTierKey: (t: MediaTierKey) => void;
  setIsTestRun: (v: boolean) => void;
  setTestRunLimit: (n: number) => void;
  setCurrentRunId: (id: string | null) => void;
  setUserNote: (v: string) => void;
  setUserText: (v: string) => void;
  setStyle: (v: string) => void;
  setSelectedColumns: (cols: string[]) => void;
  toggleColumn: (col: string) => void;
  setImageCount: (n: number) => void;
  reset: () => void;
}

const DEFAULTS = {
  mode: 'image_search' as MediaMode,
  datasetId: null as string | null,
  tierKey: 'optimal' as MediaTierKey,
  isTestRun: true,
  testRunLimit: 2,
  currentRunId: null as string | null,
  userNote: '',
  userText: '',
  style: '',
  selectedColumns: [] as string[],
  imageCount: 1,
};

export const useMediaStore = create<MediaState>()(
  persist(
    (set) => ({
      ...DEFAULTS,
      setMode: (m) => set({ mode: m }),
      setDatasetId: (id) => set({ datasetId: id }),
      setTierKey: (t) => set({ tierKey: t }),
      setIsTestRun: (v) => set({ isTestRun: v }),
      setTestRunLimit: (n) => set({ testRunLimit: Math.max(1, Math.min(3, n)) }),
      setCurrentRunId: (id) => set({ currentRunId: id }),
      setUserNote: (v) => set({ userNote: v }),
      setUserText: (v) => set({ userText: v }),
      setStyle: (v) => set({ style: v }),
      setSelectedColumns: (cols) => set({ selectedColumns: cols }),
      toggleColumn: (col) =>
        set((state) => ({
          selectedColumns: state.selectedColumns.includes(col)
            ? state.selectedColumns.filter((c) => c !== col)
            : [...state.selectedColumns, col],
        })),
      setImageCount: (n) => set({ imageCount: Math.max(1, Math.min(10, n)) }),
      reset: () => set({ ...DEFAULTS }),
    }),
    { name: 'cs-media-store' },
  ),
);

export const clampTestRunLimit = (n: number): number => {
  const v = Number.isFinite(n) ? Math.round(n) : 2;
  if (v >= 3) return 3;
  return 2;
};
